"use client";

import { getVirtualMachineConfigFromAddress } from "@/config/chains/helpers";

import { useGetV3CustomerAddresses } from "@/gen/orval/webplatform-v3/endpoints/customer-addresses/customer-addresses.gen";

import { useGetAllNetworks } from "@/subsystems/blockchain/networks/hooks";
import type { CustomerAddress, EntitlementType } from "@/subsystems/transfer/accounts/types";
import {
  hasLiveEntitlementOfType,
  isInstantSettlementAvailable,
  toCustomerAddress,
} from "@/subsystems/transfer/accounts/utils/customer-address-utils";

export type GetSingleBlockchainAddress = UseGetSingleBlockchainAddress["blockchainAddress"];

export type UseGetSingleBlockchainAddress = ReturnType<typeof useGetSingleBlockchainAddress>;

// Re-exported so the many callers that import it from here keep working; it lives in the utils
// module because `getCoveredChainIds` needs it and utils cannot import from a hook.
export { isInstantSettlementAvailable };

/** `entitlementTypes` is required so every caller says which addresses it means. */
export function useGetAllBlockchainAddresses({ entitlementTypes }: { entitlementTypes: readonly EntitlementType[] }) {
  const { networks } = useGetAllNetworks();
  const v3AddressesQuery = useGetV3CustomerAddresses();

  const queries = [v3AddressesQuery, networks];

  const statuses = {
    error: queries.find((c) => c.isError)?.error ?? null,
    isError: queries.some((c) => c.isError),
    isFetching: queries.some((c) => c.isFetching),
    isLoading: queries.some((c) => c.isLoading),
    isPending: queries.some((c) => c.isPending),
    isSuccess: queries.every((c) => c.isSuccess),
  };

  if (!statuses.isSuccess || !v3AddressesQuery.data || !networks.data) {
    return {
      blockchainAddresses: {
        data: null,
        ...statuses,
      },
    };
  }

  // Transform to CustomerAddress (minimal transform - just add chainId and VM config)
  // Filter out:
  // 1. Addresses where ALL listed entitlements are fully removed (soft-deleted)
  // 2. Addresses carrying no listed entitlement
  // Note: pending_removal is kept visible — the table renders a "Pending removal" badge.
  const customerAddresses: CustomerAddress[] = v3AddressesQuery.data
    .map((v3Address) => {
      const virtualMachineConfig = getVirtualMachineConfigFromAddress(v3Address.address);
      return toCustomerAddress(v3Address, virtualMachineConfig);
    })
    .filter((address) => hasLiveEntitlementOfType(address, entitlementTypes))
    .sort((a, b) => (a.nickname ?? "").localeCompare(b.nickname ?? ""));

  return {
    blockchainAddresses: {
      data: customerAddresses,
      ...statuses,
    },
  };
}

export function useGetSingleBlockchainAddress({
  entitlementTypes,
  pk,
}: {
  entitlementTypes: readonly EntitlementType[];
  pk: string;
}) {
  const { blockchainAddresses } = useGetAllBlockchainAddresses({ entitlementTypes });

  // Support lookup by address.id (primary) or entitlement ID (for backward compat)
  const filteredBlockchainAddress =
    blockchainAddresses?.data?.find((address) => address.id === pk || address.entitlements.some((e) => e.id === pk)) ??
    null;

  return {
    allBlockchainAddresses: blockchainAddresses,
    blockchainAddress: {
      data: filteredBlockchainAddress,
      error: blockchainAddresses.error,
      isError: blockchainAddresses.isError,
      isFetching: blockchainAddresses.isFetching,
      isLoading: blockchainAddresses.isLoading,
      isPending: blockchainAddresses.isPending,
      isSuccess: blockchainAddresses.isSuccess,
    },
  };
}
